// Turns a run plus the option controls into a Clip (which bins and slots are
// in play, how rows map to time) and a View (which part of the clip fills the
// canvas). Every coordinate conversion the painters, tape, gutter, tooltip
// and export need lives here.
//
// Coordinates:
//   col  bin index within the run, fractional between bin centers
//   row  either a cadence slot (uncompressed) or a compressed pixel row
//   x,y  CSS pixels on the heat canvas

import { MISSING, type Run } from '../model.ts'
import { floorToDay, floorToHour, zonedNaiveToEpochMs } from '../tz.ts'
import type { TimeInput } from '../units.ts'

export interface Options {
  /**
   * The dB range, or null for auto at either end. Not part of the clip: it
   * decides color, not which data is shown, so it is resolved with
   * `clampDbRange` at paint time and moving it repaints without rebuilding
   * anything.
   */
  dbMin: number | null
  dbMax: number | null
  lowHz: number | null
  highHz: number | null
  begin: TimeInput | null
  end: TimeInput | null
  headSec: number | null
  tailSec: number | null
  /** 0 = off, >= 1 = target pixel height, 0 < x < 1 = fraction of the rows. */
  compress: number
  offsetHz: number
  ytickSec: number | null
  captureZone: string
}

export interface Clip {
  run: Run
  offsetHz: number
  /** Bin range, half-open. */
  bin0: number
  bin1: number
  /** Slot range, half-open. */
  slot0: number
  slot1: number
  /** Row count on the time axis after compression. */
  rows: number
  compressed: boolean
  /** Row of each slot in the clip (index = slot - slot0). */
  rowOfSlot: Int32Array
  /** First slot mapped into each row (index = row). */
  slotOfRow: Int32Array
  /** Compressed rows hold the mean of several slots; MISSING-aware. */
  compressedGrid: Int16Array | null
  startMs: number
  endMs: number
}

const EMPTY_CLIP_ROWS = 0

function resolveTime(input: TimeInput, zone: string): number {
  return input.kind === 'epoch' ? input.ms : zonedNaiveToEpochMs(input.naive, zone)
}

/**
 * The displayed frequency span the run actually covers: the first bin's start
 * to the last bin's end, after the offset. This is the range a crop is clamped
 * to, and what the footer prints.
 */
export function displayedBand(run: Run, offsetHz: number): { lowHz: number; highHz: number } {
  return {
    lowHz: run.binHz[0]! + offsetHz,
    highHz: run.binHz[run.binCount - 1]! + offsetHz + run.geometry.stepHz,
  }
}

/** First index with `binHz[i] >= target`. */
function lowerBound(binHz: Float64Array, target: number): number {
  let lo = 0
  let hi = binHz.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (binHz[mid]! < target) lo = mid + 1
    else hi = mid
  }
  return lo
}

/** First index with `binHz[i] > target`. */
function upperBound(binHz: Float64Array, target: number): number {
  let lo = 0
  let hi = binHz.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (binHz[mid]! <= target) lo = mid + 1
    else hi = mid
  }
  return lo
}

/**
 * Bins whose displayed frequency lies within [lowHz, highHz], clamped to the
 * data the run actually holds.
 *
 * Clamping is not defensive tidying: the crop persists across files, so a
 * 902-928 MHz crop left over from a 33cm capture would otherwise select no bins
 * at all in a 2m one, blanking the view with no way to see what went wrong. A
 * bound outside the captured band selects the nearest edge instead, and a
 * window falling between two bin centers selects the nearer of them, so the
 * result is never empty.
 */
function binRange(run: Run, offsetHz: number, lowHz: number | null, highHz: number | null): [number, number] {
  const first = run.binHz[0]!
  const last = run.binHz[run.binCount - 1]!
  const clamp = (hz: number) => Math.min(Math.max(hz - offsetHz, first), last)

  let low = lowHz === null ? first : clamp(lowHz)
  let high = highHz === null ? last : clamp(highHz)
  // A pair entered the wrong way round reads as the range between them, which
  // is the only interpretation that is not an empty selection.
  if (low > high) [low, high] = [high, low]

  const bin0 = lowerBound(run.binHz, low)
  const bin1 = upperBound(run.binHz, high)
  if (bin1 > bin0) return [bin0, bin1]

  // Both bounds landed in the same gap between bin centers; take the nearer bin.
  const mid = (low + high) / 2
  const above = Math.min(bin0, run.binCount - 1)
  const below = Math.max(0, above - 1)
  const nearest = Math.abs(run.binHz[above]! - mid) <= Math.abs(run.binHz[below]! - mid) ? above : below
  return [nearest, nearest + 1]
}

export function slotTimeMs(run: Run, slot: number): number {
  return run.tsMs[0]! + slot * run.cadenceMs
}

function slotRange(run: Run, options: Options): [number, number] {
  const zone = options.captureZone
  let beginMs = options.begin ? resolveTime(options.begin, zone) : null
  let endMs = options.end ? resolveTime(options.end, zone) : null
  const runStart = slotTimeMs(run, 0)
  const runEnd = slotTimeMs(run, run.slotCount - 1)
  if (options.headSec !== null) endMs = (beginMs ?? runStart) + options.headSec * 1000
  if (options.tailSec !== null) beginMs = (endMs ?? runEnd) - options.tailSec * 1000

  let slot0 = 0
  let slot1 = run.slotCount
  if (beginMs !== null) slot0 = Math.max(0, Math.ceil((beginMs - runStart) / run.cadenceMs))
  if (endMs !== null) slot1 = Math.min(run.slotCount, Math.floor((endMs - runStart) / run.cadenceMs) + 1)
  if (slot1 <= slot0) {
    // An empty selection; fall back to a single slot at the nearer end so the
    // view always has something to show rather than dividing by zero.
    slot0 = Math.min(Math.max(0, slot0), run.slotCount - 1)
    slot1 = slot0 + 1
  }
  return [slot0, slot1]
}

/** heatmap.py's `time_compression`: an asymptotic squeeze of older rows. */
export function timeCompression(y: number, decay: number): number {
  return Math.round((1 / decay) * Math.exp(y * decay) - 1 / decay)
}

/**
 * heatmap.py's compression parameter handling. Returns the decay constant and
 * pixel height, or null when compression is off or the image is too short.
 */
export function compressionPlan(compress: number, height: number): { decay: number; pixHeight: number } | null {
  if (!(compress > 0)) return null
  let target = compress
  if (target > height) return null
  if (target < 1) target *= height
  if (!(target > 0)) return null
  const decay = -1 / target
  return { decay, pixHeight: timeCompression(height, decay) }
}

/**
 * The dB range to color against: the pinned values where given, the run's own
 * extremes where not, clamped to those extremes either way.
 *
 * Clamped for the same reason the frequency crop is. The setting persists
 * across files, and a range carried over from a stronger capture would flatten
 * this one into a single color with nothing on screen to say why. It also
 * keeps the sliders honest: the track covers the data, so every position on it
 * means what it shows.
 */
export function clampDbRange(run: Run, dbMin: number | null, dbMax: number | null): [number, number] {
  const clamp = (db: number) => Math.min(Math.max(db, run.minDb), run.maxDb)
  return [clamp(dbMin ?? run.minDb), clamp(dbMax ?? run.maxDb)]
}

export function buildClip(run: Run, options: Options): Clip {
  const [bin0, bin1] = binRange(run, options.offsetHz, options.lowHz, options.highHz)
  const [slot0, slot1] = slotRange(run, options)
  const slots = slot1 - slot0

  const plan = compressionPlan(options.compress, slots)
  let rows: number
  const rowOfSlot = new Int32Array(slots)
  let slotOfRow: Int32Array
  let compressedGrid: Int16Array | null = null

  if (plan) {
    rows = Math.max(1, plan.pixHeight)
    slotOfRow = new Int32Array(rows).fill(-1)
    for (let i = 0; i < slots; i++) {
      const row = Math.min(rows - 1, Math.max(0, plan.pixHeight - timeCompression(slots - i, plan.decay)))
      rowOfSlot[i] = row
      if (slotOfRow[row] === -1) slotOfRow[row] = slot0 + i
    }
    // Rows that no slot mapped to inherit the nearest earlier slot so lookups stay total.
    for (let r = 1; r < rows; r++) if (slotOfRow[r] === -1) slotOfRow[r] = slotOfRow[r - 1]!
    if (slotOfRow[0] === -1) slotOfRow[0] = slot0
    compressedGrid = compressRows(run, bin0, bin1, slot0, slots, rows, rowOfSlot)
  } else {
    rows = slots
    slotOfRow = new Int32Array(rows)
    for (let i = 0; i < slots; i++) {
      rowOfSlot[i] = i
      slotOfRow[i] = slot0 + i
    }
  }

  return {
    run,
    offsetHz: options.offsetHz,
    bin0,
    bin1,
    slot0,
    slot1,
    rows: rows || EMPTY_CLIP_ROWS,
    compressed: plan !== null,
    rowOfSlot,
    slotOfRow,
    compressedGrid,
    startMs: slotTimeMs(run, slot0),
    endMs: slotTimeMs(run, slot1 - 1),
  }
}

/** Mean of the slots that share a compressed row, ignoring MISSING, as heatmap.py averages. */
function compressRows(
  run: Run,
  bin0: number,
  bin1: number,
  slot0: number,
  slots: number,
  rows: number,
  rowOfSlot: Int32Array,
): Int16Array {
  const cols = bin1 - bin0
  const sum = new Float64Array(rows * cols)
  const count = new Uint16Array(rows * cols)
  for (let i = 0; i < slots; i++) {
    const sweep = run.slotSweep[slot0 + i]!
    if (sweep === -1) continue
    const row = rowOfSlot[i]!
    const src = sweep * run.binCount + bin0
    const dst = row * cols
    for (let c = 0; c < cols; c++) {
      const v = run.dbm[src + c]!
      if (v === MISSING) continue
      sum[dst + c] = sum[dst + c]! + v
      count[dst + c] = count[dst + c]! + 1
    }
  }
  const out = new Int16Array(rows * cols)
  for (let i = 0; i < out.length; i++) out[i] = count[i]! > 0 ? Math.round(sum[i]! / count[i]!) : MISSING
  return out
}

/** Centi-dB at a clip row and run bin, MISSING for gaps. */
export function clipCell(clip: Clip, row: number, bin: number): number {
  if (bin < clip.bin0 || bin >= clip.bin1 || row < 0 || row >= clip.rows) return MISSING
  if (clip.compressedGrid) return clip.compressedGrid[row * (clip.bin1 - clip.bin0) + (bin - clip.bin0)]!
  const sweep = clip.run.slotSweep[clip.slot0 + row]!
  if (sweep === -1) return MISSING
  return clip.run.dbm[sweep * clip.run.binCount + bin]!
}

export function rowTimeMs(clip: Clip, row: number): number {
  const r = Math.min(clip.rows - 1, Math.max(0, Math.floor(row)))
  return slotTimeMs(clip.run, clip.slotOfRow[r]!)
}

/** Instant at a fractional row, interpolating between the slots rows map to. */
export function rowToMs(clip: Clip, row: number): number {
  const r = Math.min(clip.rows - 1, Math.max(0, row))
  const i = Math.floor(r)
  const f = r - i
  const a = slotTimeMs(clip.run, clip.slotOfRow[i]!)
  const next = i + 1 < clip.rows ? clip.slotOfRow[i + 1]! : clip.slotOfRow[i]! + 1
  const b = slotTimeMs(clip.run, next)
  return a + f * (b - a)
}

/** Fractional row for an instant; rows outside the clip extrapolate. */
export function timeToRow(clip: Clip, ms: number): number {
  const slot = (ms - slotTimeMs(clip.run, 0)) / clip.run.cadenceMs - clip.slot0
  if (!clip.compressed) return slot
  const i = Math.floor(slot)
  if (i < 0) return slot
  if (i >= clip.rowOfSlot.length) return clip.rows + (slot - clip.rowOfSlot.length)
  const row = clip.rowOfSlot[i]!
  const next = i + 1 < clip.rowOfSlot.length ? clip.rowOfSlot[i + 1]! : row + 1
  return row + (slot - i) * (next - row)
}

/** Displayed frequency at a fractional column, interpolating between bin centers. */
export function colToHz(clip: Clip, col: number): number {
  const { binHz } = clip.run
  const n = binHz.length
  const step = clip.run.geometry.stepHz
  if (col <= 0) return binHz[0]! + col * step + clip.offsetHz
  if (col >= n - 1) return binHz[n - 1]! + (col - (n - 1)) * step + clip.offsetHz
  const i = Math.floor(col)
  const f = col - i
  return binHz[i]! + f * (binHz[i + 1]! - binHz[i]!) + clip.offsetHz
}

/** Fractional column for a displayed frequency. Bin centers sit at integer columns. */
export function hzToCol(clip: Clip, hz: number): number {
  const { binHz } = clip.run
  const n = binHz.length
  const step = clip.run.geometry.stepHz
  const target = hz - clip.offsetHz
  if (target <= binHz[0]!) return (target - binHz[0]!) / step
  if (target >= binHz[n - 1]!) return n - 1 + (target - binHz[n - 1]!) / step
  let lo = 0
  let hi = n - 1
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1
    if (binHz[mid]! <= target) lo = mid
    else hi = mid
  }
  return lo + (target - binHz[lo]!) / (binHz[hi]! - binHz[lo]!)
}

// ------------------------------------------------------------------- view

/** The window of the clip shown on the canvas, in clip coordinates. */
export interface View {
  col0: number
  col1: number
  row0: number
  row1: number
}

const MIN_COLS = 4
const MIN_ROWS = 2

export function fullView(clip: Clip): View {
  // Cells are centered on integer coordinates; the window spans their edges.
  return { col0: clip.bin0 - 0.5, col1: clip.bin1 - 0.5, row0: -0.5, row1: clip.rows - 0.5 }
}

export function clampView(clip: Clip, view: View): View {
  const full = fullView(clip)
  let w = Math.min(view.col1 - view.col0, full.col1 - full.col0)
  let h = Math.min(view.row1 - view.row0, full.row1 - full.row0)
  w = Math.max(w, Math.min(MIN_COLS, full.col1 - full.col0))
  h = Math.max(h, Math.min(MIN_ROWS, full.row1 - full.row0))
  let col0 = Math.min(Math.max(view.col0, full.col0), full.col1 - w)
  let row0 = Math.min(Math.max(view.row0, full.row0), full.row1 - h)
  return { col0, col1: col0 + w, row0, row1: row0 + h }
}

export function zoomView(clip: Clip, view: View, fx: number, fy: number, factorX: number, factorY: number): View {
  // (fx, fy) is the anchor as a 0..1 fraction of the canvas; it stays put.
  const w = (view.col1 - view.col0) * factorX
  const h = (view.row1 - view.row0) * factorY
  const ax = view.col0 + fx * (view.col1 - view.col0)
  const ay = view.row0 + fy * (view.row1 - view.row0)
  return clampView(clip, { col0: ax - fx * w, col1: ax + (1 - fx) * w, row0: ay - fy * h, row1: ay + (1 - fy) * h })
}

export function panView(clip: Clip, view: View, dCols: number, dRows: number): View {
  return clampView(clip, {
    col0: view.col0 + dCols,
    col1: view.col1 + dCols,
    row0: view.row0 + dRows,
    row1: view.row1 + dRows,
  })
}

export function xToCol(view: View, x: number, width: number): number {
  return view.col0 + (x / width) * (view.col1 - view.col0)
}
export function colToX(view: View, col: number, width: number): number {
  return ((col - view.col0) / (view.col1 - view.col0)) * width
}
export function yToRow(view: View, y: number, height: number): number {
  return view.row0 + (y / height) * (view.row1 - view.row0)
}
export function rowToY(view: View, row: number, height: number): number {
  return ((row - view.row0) / (view.row1 - view.row0)) * height
}

// ----------------------------------------------------------------- ticks

export const TIME_TICK_LADDER = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 10800, 21600, 43200, 86400]

/**
 * Tick instants for the time axis. With a ytick, heatmap.py's rule: start on
 * the hour at or before the window start, step by the interval. Without one,
 * the smallest ladder step that keeps ticks at least `minPx` apart.
 */
export function timeTicks(
  startMs: number,
  endMs: number,
  zone: string,
  ytickSec: number | null,
  heightPx: number,
  minPx: number,
): { stepSec: number; ticks: number[] } {
  const spanSec = Math.max(1, (endMs - startMs) / 1000)
  let stepSec = ytickSec ?? TIME_TICK_LADDER[TIME_TICK_LADDER.length - 1]!
  if (ytickSec === null) {
    for (const step of TIME_TICK_LADDER) {
      if ((step / spanSec) * heightPx >= minPx) {
        stepSec = step
        break
      }
    }
  }
  const stepMs = stepSec * 1000
  // Day-sized steps align to midnight in the display zone, smaller ones to the hour.
  let t = stepSec >= 86400 ? floorToDay(startMs, zone) : floorToHour(startMs, zone)
  const ticks: number[] = []
  while (t < startMs) t += stepMs
  for (; t <= endMs; t += stepMs) {
    ticks.push(t)
    if (ticks.length > 10_000) break
  }
  return { stepSec, ticks }
}
